import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AdminShell } from "@/components/admin/AdminShell";
import { Loader2, TrendingUp, DollarSign, Tag, MousePointerClick } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";

type Redemption = {
  id: string; promo_code_id: string; booking_id: string | null; order_id: string | null;
  amount_discounted: number; source: string | null; attribution: any; created_at: string;
};
type Promo = { id: string; code: string; discount_type: string; discount_value: number; active: boolean };

const fmt = (n: number) => `$${(n || 0).toFixed(2)}`;

function toCsv(headers: string[], rows: (string | number)[][]): string {
  const esc = (v: string | number) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.split('"').join('""')}"` : s;
  };
  return [headers.map(esc).join(","), ...rows.map(r => r.map(esc).join(","))].join("\n");
}
function downloadCsv(name: string, csv: string) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function AdminPromoAnalytics() {
  const [loading, setLoading] = useState(true);
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [promos, setPromos] = useState<Promo[]>([]);
  const [bookings, setBookings] = useState<Record<string, { total: number; attribution: any }>>({});
  const [orders, setOrders] = useState<Record<string, { total: number; attribution: any }>>({});
  const [views, setViews] = useState<{ slug: string | null; count: number }[]>([]);

  useEffect(() => {
    (async () => {
      const [{ data: r }, { data: p }, { data: v }] = await Promise.all([
        supabase.from("promo_redemptions").select("*").order("created_at", { ascending: false }).limit(2000),
        supabase.from("promo_codes").select("id,code,discount_type,discount_value,active"),
        supabase.from("landing_page_views").select("slug"),
      ]);
      setRedemptions((r as any) ?? []);
      setPromos((p as any) ?? []);
      // View counts per slug
      const vc: Record<string, number> = {};
      for (const row of (v as any[]) ?? []) vc[row.slug ?? "(unknown)"] = (vc[row.slug ?? "(unknown)"] ?? 0) + 1;
      setViews(Object.entries(vc).map(([slug, count]) => ({ slug, count })).sort((a, b) => b.count - a.count));

      const bookingIds = (r ?? []).map((x: any) => x.booking_id).filter(Boolean);
      const orderIds = (r ?? []).map((x: any) => x.order_id).filter(Boolean);
      const [{ data: bks }, { data: ods }] = await Promise.all([
        bookingIds.length ? supabase.from("bookings").select("id,total,attribution").in("id", bookingIds) : Promise.resolve({ data: [] as any[] }),
        orderIds.length ? supabase.from("orders").select("id,total,attribution").in("id", orderIds) : Promise.resolve({ data: [] as any[] }),
      ]);
      const bm: any = {}; for (const b of (bks as any[]) ?? []) bm[b.id] = { total: Number(b.total), attribution: b.attribution };
      const om: any = {}; for (const o of (ods as any[]) ?? []) om[o.id] = { total: Number(o.total), attribution: o.attribution };
      setBookings(bm); setOrders(om);
      setLoading(false);
    })();
  }, []);

  const byPromo = useMemo(() => {
    const map = new Map<string, { code: string; redemptions: number; discount: number; revenue: number }>();
    for (const p of promos) map.set(p.id, { code: p.code, redemptions: 0, discount: 0, revenue: 0 });
    for (const r of redemptions) {
      const row = map.get(r.promo_code_id) ?? { code: "(deleted)", redemptions: 0, discount: 0, revenue: 0 };
      row.redemptions += 1;
      row.discount += Number(r.amount_discounted || 0);
      row.revenue += Number(bookings[r.booking_id ?? ""]?.total ?? orders[r.order_id ?? ""]?.total ?? 0);
      map.set(r.promo_code_id, row);
    }
    return [...map.values()].sort((a, b) => b.redemptions - a.redemptions);
  }, [promos, redemptions, bookings, orders]);

  const bySource = useMemo(() => {
    const map = new Map<string, { source: string; redemptions: number; revenue: number; discount: number }>();
    for (const r of redemptions) {
      const src = r.source || r.attribution?.utm_source || r.attribution?.landing_slug || "direct";
      const row = map.get(src) ?? { source: src, redemptions: 0, revenue: 0, discount: 0 };
      row.redemptions += 1;
      row.discount += Number(r.amount_discounted || 0);
      row.revenue += Number(bookings[r.booking_id ?? ""]?.total ?? orders[r.order_id ?? ""]?.total ?? 0);
      map.set(src, row);
    }
    return [...map.values()].sort((a, b) => b.revenue - a.revenue);
  }, [redemptions, bookings, orders]);

  const byLanding = useMemo(() => {
    // join views (denominator) with redemptions tagged with the same landing_slug (numerator)
    const redBySlug = new Map<string, { redemptions: number; revenue: number; discount: number }>();
    for (const r of redemptions) {
      const slug = r.attribution?.landing_slug;
      if (!slug) continue;
      const row = redBySlug.get(slug) ?? { redemptions: 0, revenue: 0, discount: 0 };
      row.redemptions += 1;
      row.discount += Number(r.amount_discounted || 0);
      row.revenue += Number(bookings[r.booking_id ?? ""]?.total ?? orders[r.order_id ?? ""]?.total ?? 0);
      redBySlug.set(slug, row);
    }
    return views.map(v => {
      const r = redBySlug.get(v.slug ?? "") ?? { redemptions: 0, revenue: 0, discount: 0 };
      const cr = v.count > 0 ? (r.redemptions / v.count) * 100 : 0;
      return { slug: v.slug ?? "(unknown)", views: v.count, ...r, conversion_rate: cr };
    }).sort((a, b) => b.revenue - a.revenue);
  }, [views, redemptions, bookings, orders]);

  const totals = useMemo(() => ({
    redemptions: redemptions.length,
    discount: redemptions.reduce((s, r) => s + Number(r.amount_discounted || 0), 0),
    revenue: redemptions.reduce((s, r) => s + Number(bookings[r.booking_id ?? ""]?.total ?? orders[r.order_id ?? ""]?.total ?? 0), 0),
    views: views.reduce((s, v) => s + v.count, 0),
  }), [redemptions, bookings, orders, views]);

  return (
    <AdminShell title="Promo Analytics">
      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Stat icon={<Tag className="w-4 h-4" />} label="Redemptions" value={String(totals.redemptions)} />
            <Stat icon={<DollarSign className="w-4 h-4" />} label="Revenue (gross)" value={fmt(totals.revenue)} />
            <Stat icon={<TrendingUp className="w-4 h-4" />} label="Discount given" value={fmt(totals.discount)} />
            <Stat icon={<MousePointerClick className="w-4 h-4" />} label="Landing views" value={String(totals.views)} />
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            <Button variant="outline" size="sm" onClick={() => downloadCsv("promo-by-code.csv",
              toCsv(["code","redemptions","discount","revenue","avg_discount"],
                byPromo.map(r => [r.code, r.redemptions, r.discount.toFixed(2), r.revenue.toFixed(2),
                  (r.redemptions ? r.discount / r.redemptions : 0).toFixed(2)])))}>
              <Download className="w-4 h-4 mr-1" /> CSV: by promo
            </Button>
            <Button variant="outline" size="sm" onClick={() => downloadCsv("promo-by-utm-source.csv",
              toCsv(["source","redemptions","discount","revenue"],
                bySource.map(r => [r.source, r.redemptions, r.discount.toFixed(2), r.revenue.toFixed(2)])))}>
              <Download className="w-4 h-4 mr-1" /> CSV: by UTM source
            </Button>
            <Button variant="outline" size="sm" onClick={() => downloadCsv("promo-by-landing.csv",
              toCsv(["slug","views","redemptions","conversion_rate_pct","discount","revenue"],
                byLanding.map(r => [r.slug, r.views, r.redemptions, r.conversion_rate.toFixed(2),
                  r.discount.toFixed(2), r.revenue.toFixed(2)])))}>
              <Download className="w-4 h-4 mr-1" /> CSV: by landing page
            </Button>
            <Button variant="outline" size="sm" onClick={() => downloadCsv("promo-redemptions.csv",
              toCsv(["created_at","promo_code_id","booking_id","order_id","amount_discounted","source","utm_source","utm_campaign","landing_slug"],
                redemptions.map(r => [r.created_at, r.promo_code_id, r.booking_id ?? "", r.order_id ?? "",
                  Number(r.amount_discounted).toFixed(2), r.source ?? "",
                  r.attribution?.utm_source ?? "", r.attribution?.utm_campaign ?? "", r.attribution?.landing_slug ?? ""])))}>
              <Download className="w-4 h-4 mr-1" /> CSV: raw redemptions
            </Button>
          </div>

          <Tabs defaultValue="promo">
            <TabsList>
              <TabsTrigger value="promo">By promo</TabsTrigger>
              <TabsTrigger value="source">By UTM source</TabsTrigger>
              <TabsTrigger value="landing">By landing page</TabsTrigger>
            </TabsList>

            <TabsContent value="promo">
              <Table headers={["Code", "Redemptions", "Discount", "Revenue", "Avg discount"]}
                rows={byPromo.map(r => [r.code, r.redemptions, fmt(r.discount), fmt(r.revenue), fmt(r.redemptions ? r.discount / r.redemptions : 0)])} />
            </TabsContent>
            <TabsContent value="source">
              <Table headers={["Source", "Redemptions", "Discount", "Revenue"]}
                rows={bySource.map(r => [r.source, r.redemptions, fmt(r.discount), fmt(r.revenue)])} />
            </TabsContent>
            <TabsContent value="landing">
              <Table headers={["Slug", "Views", "Redemptions", "Conv. rate", "Revenue"]}
                rows={byLanding.map(r => [r.slug, r.views, r.redemptions, `${r.conversion_rate.toFixed(2)}%`, fmt(r.revenue)])} />
            </TabsContent>
          </Tabs>
        </>
      )}
    </AdminShell>
  );
}

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="bg-surface border border-border p-4">
      <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest font-mono">{icon} {label}</div>
      <div className="font-display text-2xl mt-2">{value}</div>
    </div>
  );
}

function Table({ headers, rows }: { headers: string[]; rows: (string | number)[][] }) {
  return (
    <div className="bg-surface border border-border mt-3">
      <table className="w-full text-sm">
        <thead className="bg-panel">
          <tr>{headers.map(h => <th key={h} className="text-left p-2 font-mono text-xs uppercase tracking-widest">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={headers.length} className="p-6 text-center text-muted-foreground">No data yet.</td></tr>
          ) : rows.map((r, i) => (
            <tr key={i} className="border-t border-border">
              {r.map((c, j) => <td key={j} className="p-2">{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}